import { sumDecimalStrings } from '@/lib/decimal';
import {
  currenciesUsedBy,
  cycleContaining,
  paidFigureOf,
  revenueFor,
  revenueTotalFor,
  todayIso,
  type RevenueCurrency,
  type RevenueCycle,
} from '@/lib/use-revenue-analytics';
import type { RevenueTrackerRow, RevenueTrackerService } from '@/lib/types';
import type { MatrixCycle, MatrixLine } from '@/lib/revenue-matrix';

/**
 * THE ARITHMETIC AND THE VOCABULARY BEHIND THE **Revenue by Company** TAB.
 *
 * ⚠️ **A `.ts`, NOT A `.tsx`, AND THAT IS THE LINT RULE RATHER THAN TASTE.**
 * `react(only-export-components)` warns on any file that exports both a
 * component and something else, so every constant, type and pure function this
 * tab shares lives here and the four `.tsx` files beside it export components
 * and nothing else. That is the same split `financial-shared.tsx` failed to
 * make and is warned about for.
 *
 * ── 🔴 WHAT EVERY FIGURE ON THIS TAB IS, AND WHAT IT IS NOT ────────────────
 *
 * Read `@/lib/use-revenue-analytics` first — it carries the rules. The three
 * that decide the shape of everything here:
 *
 * 1. 🔴 **ONE UNIT AT A TIME, AND SINCE 2026-09-18 THE SERVER CONVERTS INTO IT
 *    RATHER THAN FILTERING TO IT.** ⚠️ *SUPERSEDED, in two steps: "**ONE
 *    CURRENCY AT A TIME**, and that is a SCOPE DECISION rather than a missing
 *    capability … `f_exchange_rates` exists (backend migration 178) and
 *    **Revenue by Product** converts through it; **this tab was deliberately
 *    left filtering.** Every helper below takes the chosen currency and filters
 *    to it before summing. A company billed in two currencies appears under both
 *    with a different figure under each."*
 *
 *    **The conversion was carried across on the owner's instruction.** Every
 *    helper below still takes ONE unit and still admits only lines stating it —
 *    the code did not change and does not need to — but that unit is now
 *    **`meta.measuredInCurrency`, the server's ECHO**, and on a converted
 *    response every line states it. So the same test stops hiding money and
 *    becomes the check that conversion happened; see `revenueFor`, which is the
 *    one gate every figure here passes through.
 *
 *    ⛔ **PASS THE ECHO, NEVER THE PICKER'S VALUE.** A server predating
 *    `baseCurrency` ignores the key and answers `200` with unconverted rows, and
 *    a caller comparing against its own request would admit dollars to a
 *    sterling total, silently. The panel resolves it once — `figuresCurrency`.
 *
 *    ⚠️ **{@link ScopedRow.alsoBilledIn} IS EMPTY ON A CONVERTED RESPONSE AND
 *    STILL LOAD-BEARING ON AN UNCONVERTED ONE** — it follows the data rather
 *    than a flag, so it withdraws itself exactly when the population it
 *    describes stops existing.
 * 2. **`null` IS "NOTHING IN THIS CURRENCY", NEVER `"0.00"`.** A company that
 *    took no dollars and one that took exactly zero dollars are different facts.
 *    Nothing here coalesces one into the other, and every renderer draws the
 *    first as an em dash.
 * 3. **PAID INVOICES ONLY, CASH COLLECTED INCLUDING TAX** (owner, 2026-10-16;
 *    ⚠️ *SUPERSEDED: "EX-TAX … the server sums `line_subtotal`, so VAT is not
 *    either"*). Never captioned "invoiced" or "billed" — a raised invoice nobody
 *    has paid is not in any of these figures. Every line's figure is
 *    `paidFigureOf` (its `amountInclTax`) in `@/lib/use-revenue-analytics`, and
 *    every label reads `Paid, incl. tax`.
 *
 * ── ⚠️ MONEY IS AN EXACT DECIMAL STRING, END TO END ────────────────────────
 *
 * Every amount stays the string it arrived as and is grouped for display by
 * `formatDecimalString`. `Number()` appears in exactly two places in this tab —
 * {@link shareOf} and the bar geometry that reads it — and in both it produces
 * a RATIO or a width, never a figure anybody reads. `Number("18000.10") * 100`
 * is not `1801000`, and a total built that way disagrees with the database by a
 * cent, which is the disagreement Xero surfaces.
 */

/* -------------------------------------------------------------------------
 * The filter vocabulary
 * --------------------------------------------------------------------- */

/**
 * A filter's "everything" value.
 *
 * The empty string, so a default is spelled by its URL key being ABSENT — one
 * canonical address per view, and "no param" and "the default" are one state
 * rather than two spellings of it. Same convention as `RevenueTrackerPage`.
 */
export const ALL = '';

/**
 * A multi-value filter key read off the URL (owner 2026-10-28: every filter
 * takes several values). CSV, deduplicated, blanks dropped; absent or empty is
 * "all" (`[]`). A link written before the change names one value and parses to
 * a one-element list, so it keeps meaning what it meant. Every key these tabs
 * filter by (billing-region slugs, `(none)`, `id:<n>`) is comma-free.
 */
export function parseKeyList(raw: string | null): string[] {
  if (raw === null) return [];
  return [...new Set(raw.split(',').map((part) => part.trim()).filter((part) => part !== ''))];
}

/** The URL spelling of {@link parseKeyList}'s input — `ALL` (so: absent) when empty. */
export function keyListParam(values: readonly string[]): string {
  return values.length === 0 ? ALL : values.join(',');
}

/**
 * The band key for a company that resolves no billing region.
 *
 * 🔴 **`billingRegion: null` IS AN ORDINARY STATE AND NOT A FAULT.**
 * `d_companies.country_id` is nullable and was never backfilled, so a real
 * share of live trading accounts land here. Giving it a key of its own is what
 * lets it be a named option in the picker and a labelled group on screen; left
 * blank it reads as a rendering bug and sends somebody hunting for a broken
 * join.
 *
 * ⚠️ **THE PARENTHESES ARE LOAD-BEARING.** This value goes into the URL beside
 * real slugs, and a real slug is the server's `billing_region` ENUM member —
 * lower-case letters and underscores (`united_kingdom`, `rest_of_world`). No
 * ENUM member can contain a bracket, so this sentinel cannot ever be mistaken
 * for one. Do not "tidy" it to `none`, which is a spelling a future region
 * could legitimately take.
 */
export const NO_REGION_KEY = '(none)';

/** What that band is called wherever it is named. Written once so it cannot drift. */
export const NO_REGION_LABEL = 'No billing region recorded';

/**
 * A row's band key.
 *
 * ⚠️ **MATCHED ON `slug`, NEVER ON `name`.** The name is editable wording; the
 * slug is the server's own join key and is write-on-create-only. Matching on
 * the name means a region renamed by an administrator silently stops matching
 * the filter somebody saved a link to.
 */
export function regionKeyOf(row: RevenueTrackerRow): string {
  return row.billingRegion?.slug ?? NO_REGION_KEY;
}

/** What the reader calls a row's band. */
export function regionLabelOf(row: RevenueTrackerRow): string {
  return row.billingRegion?.name ?? NO_REGION_LABEL;
}

/** One option in the billing-region picker. */
export interface RegionOption {
  value: string;
  label: string;
}

/**
 * Every band present in the data, alphabetically, with the no-band group last.
 *
 * ⚠️ **BUILT FROM THE ROWS, so the picker cannot offer a band that matches
 * nothing** — a filter that reliably returns an empty page reads as a broken
 * report rather than as a question nobody should have been asked.
 *
 * The no-band group sorts last wherever it appears, on this tab and in the
 * table's own grouping, so its position is one decision made once.
 */
export function regionOptionsOf(rows: readonly RevenueTrackerRow[]): RegionOption[] {
  const seen = new Map<string, string>();
  for (const row of rows) seen.set(regionKeyOf(row), regionLabelOf(row));

  return [...seen]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => {
      if (a.value === NO_REGION_KEY) return 1;
      if (b.value === NO_REGION_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
}

/* -------------------------------------------------------------------------
 * 🔴 Filtering by PRODUCT — and why it is NOT built on `serviceName`
 * --------------------------------------------------------------------- */

/**
 * The product key for a paid line that names no catalogue product.
 *
 * 🔴 **AN ORDINARY STATE, NOT A FAULT.** `f_invoice_products.product_id` is
 * nullable and imported Xero lines routinely carry none, so a real share of the
 * money lands here. Giving it a key of its own is what lets it be a NAMED row in
 * the picker — owner's decision, 2026-09-14, and the same call the Revenue by
 * Product tab already takes for its uncatalogued bucket. **Dropping it from the
 * list would make that revenue unreachable from this tab with nothing on screen
 * saying it exists.**
 *
 * ⚠️ **THE SPACE AND THE PARENTHESES ARE LOAD-BEARING**, exactly as on
 * {@link NO_REGION_KEY}: this value sits in the URL beside real keys, and a real
 * key is `id:` followed by digits. No such key can contain a bracket, so the
 * sentinel can never collide with one. Do not "tidy" it.
 */
export const NO_PRODUCT_KEY = '(no product)';

/** What that bucket is called wherever it is named. Written once so it cannot drift. */
export const NO_PRODUCT_LABEL = 'No product recorded';

/**
 * A paid line's product key — or `null` for **"the server did not say"**.
 *
 * 🔴 **THE THREE STATES OF `service.product` ARE TOLD APART HERE AND NOWHERE
 * ELSE.** `undefined` is an API predating the key, `null` is a line that names
 * no product, and an object is a product. Folding the first two together would
 * file **every line in the estate** under {@link NO_PRODUCT_LABEL} the moment
 * the server is rolled back — a confident wrong answer rather than a missing
 * one, and one that looks entirely plausible on screen.
 *
 * ⚠️ **KEYED ON THE ID, NEVER THE NAME.** The name is editable wording; the id
 * is the row. This repo has shipped three defects by matching admin-editable
 * text, and a product an administrator renames must not silently stop matching
 * a filter somebody saved a link to. The `id:` prefix keeps the real keys and
 * the sentinel in one namespace without either being able to spell the other.
 */
export function productKeyOfService(service: RevenueTrackerService): string | null {
  const product = service.product;
  if (product === undefined) return null;
  if (product === null) return NO_PRODUCT_KEY;
  return `id:${product.id}`;
}

/**
 * What a line's product is called.
 *
 * ⚠️ **FALLS BACK TO THE ID RATHER THAN RENDERING A BLANK.** `product_name` is
 * `NOT NULL` on the master, so an absent name could only be a broken foreign
 * key — and a blank option in a picker reads as a rendering fault and sends the
 * reader hunting a bug instead of a product. Same treatment `productLabelOf`
 * gives it on the Revenue by Product tab.
 */
function productLabelOfService(service: RevenueTrackerService): string {
  const product = service.product;
  if (product === undefined || product === null) return NO_PRODUCT_LABEL;
  const name = product.name?.trim() ?? '';
  return name === '' ? `Product #${product.id}` : name;
}

/** One option in the product picker. */
export interface ProductOption {
  value: string;
  label: string;
}

/**
 * Every product present in the loaded revenue, alphabetically, with the
 * no-product bucket last.
 *
 * ⚠️ **BUILT FROM THE PAID LINES, so the picker cannot offer a product that
 * matches nothing** — a filter that reliably empties the table reads as a broken
 * report rather than as a question nobody should have been asked. It is the same
 * rule {@link regionOptionsOf} follows, and it is why the catalogue itself is
 * deliberately NOT fetched: a product nobody has ever been invoiced for is not a
 * question this tab can answer.
 *
 * 🔴 **AN EMPTY LIST MEANS "THE SERVER REPORTS NO PRODUCTS AT ALL", AND THE
 * CONTROL IS WITHHELD ON IT.** That is the `undefined` state above, and the
 * caller must also force the held key back to {@link ALL} when it happens —
 * otherwise a pasted `?rcProduct=` narrows the table to nothing with no control
 * on screen able to clear it, which is the invisible active filter this app
 * refuses everywhere.
 *
 * ⚠️ **TAKEN OVER EVERY CYCLE AND EVERY CURRENCY**, deliberately: the picker
 * offers what the company estate has ever bought, so moving the cycle or the
 * currency does not make options appear and disappear underneath the reader.
 */
export function productOptionsOf(rows: readonly RevenueTrackerRow[]): ProductOption[] {
  const seen = new Map<string, string>();

  for (const row of rows) {
    for (const cell of Object.values(row.cycles)) {
      for (const service of cell?.services ?? []) {
        const key = productKeyOfService(service);
        if (key === null) continue;
        seen.set(key, productLabelOfService(service));
      }
    }
  }

  return [...seen]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => {
      if (a.value === NO_PRODUCT_KEY) return 1;
      if (b.value === NO_PRODUCT_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
}

/**
 * One cycle cell's total, recomputed from the lines it holds.
 *
 * ⚠️ **THIS MIRRORS THE SERVER'S `totalFor` AND IS DELIBERATELY NOT A SECOND
 * OPINION ABOUT IT**: empty is no figure, more than one currency is no figure —
 * the endpoint refusing to add pounds to dollars — and otherwise the exact sum
 * in the one currency present. It exists only so a row narrowed by
 * {@link rowsForProduct} is INTERNALLY CONSISTENT; leaving the server's total in
 * place would leave a figure describing lines the cell no longer holds, which is
 * a stale number waiting for the first reader who trusts it.
 *
 * ⚠️ **NOTHING ON THIS TAB READS IT TODAY** — every figure here is summed from
 * `services` by `revenueFor`, which is exactly why the projection below is
 * complete. This keeps the shape honest for the next reader rather than for a
 * current one.
 */
function cellTotalOf(services: readonly RevenueTrackerService[]): {
  total: string | null;
  totalCurrency: string | null;
} {
  if (services.length === 0) return { total: null, totalCurrency: null };

  const currencies = new Set(services.map((service) => service.currency));
  if (currencies.size > 1) return { total: null, totalCurrency: null };

  const [only] = currencies;
  return {
    total: sumDecimalStrings(services.map(paidFigureOf)),
    totalCurrency: only ?? null,
  };
}

/**
 * The rows as they would be if this company had only ever bought this product.
 *
 * 🔴 **THE WHOLE PRODUCT FILTER IS THIS ONE FUNCTION, AND THAT IS THE DESIGN
 * RATHER THAN A SHORTCUT.** Every figure on this tab — the table, the four
 * headline tiles, the total, the median, the concentration, the per-cycle
 * detail, the "also billed in" mark, the revenue floor and "still trading" —
 * is derived from a row's `services` by `revenueFor`. Narrowing `services` once,
 * here, therefore moves all of them together, and **no two of them can disagree
 * about which population they describe.** The alternative — teaching each helper
 * about products — is eight places to keep in step and is how a tile comes to
 * count money the table has hidden.
 *
 * ⚠️ **THE FILTER NARROWS THE FIGURES, NOT JUST THE COMPANY LIST — owner's
 * decision, 2026-09-14.** Asking *who buys this product, and how much* and being
 * shown each company's whole revenue would be a confident wrong answer under a
 * heading naming one product. The panel's `scopeLabel` names the product for the
 * same reason: a tile reading `Paid, incl. tax · USD · All cycles` over a narrowed
 * figure is that same failure one line further on.
 *
 * ⚠️ **A COMPANY THAT NEVER BOUGHT IT FALLS OUT ON ITS OWN**, with no filter of
 * its own: its cells narrow to no lines, `revenueTotalFor` answers `null`, and
 * {@link scopeRows} already drops exactly that. Nothing here has to decide what
 * an absent figure means, because nothing here invents one.
 *
 * ⚠️ **AN EMPTY LIST RETURNS THE INPUT UNTOUCHED** — the ordinary case, bit for
 * bit the behaviour that existed before this control did. Several keys (owner
 * 2026-10-28: every filter takes several values) keep every line on ANY of them.
 */
export function rowsForProduct(
  rows: readonly RevenueTrackerRow[],
  productKeys: readonly string[],
): readonly RevenueTrackerRow[] {
  if (productKeys.length === 0) return rows;
  const wanted = new Set(productKeys);

  return rows.map((row) => ({
    ...row,
    cycles: Object.fromEntries(
      Object.entries(row.cycles).map(([name, cell]) => {
        const services = (cell?.services ?? []).filter((service) => {
          const key = productKeyOfService(service);
          return key !== null && wanted.has(key);
        });
        /*
         * `notes` and every other cell field are carried through untouched —
         * a note is typed against a (company, cycle) and says nothing about
         * which products the cell holds, so narrowing must not disturb it.
         */
        return [name, { ...cell, services, ...cellTotalOf(services) }];
      }),
    ),
  }));
}

/* -------------------------------------------------------------------------
 * One company, reduced to the scope on screen
 * --------------------------------------------------------------------- */

/**
 * A company that has revenue in the chosen currency within the chosen cycle
 * scope, and what that revenue is.
 *
 * ⚠️ **`amount` IS NON-NULLABLE BY CONSTRUCTION.** {@link scopeRows} drops
 * every row whose figure is `null` rather than carrying it as an optional, so
 * nothing downstream can render a company with nothing to show and nothing has
 * to re-decide what an absent figure means. A company excluded that way is not
 * hidden silently — {@link excludedCompanyCount} counts it and the tab says so.
 */
export interface ScopedRow {
  row: RevenueTrackerRow;
  /** An exact decimal string in the chosen currency. Never `null`, never `Number`ed. */
  amount: string;
  /**
   * The OTHER currencies this company was also paid in, if any.
   *
   * 🔴 **THE ONLY THING THAT CAN SAY THE FIGURE BESIDE IT IS PARTIAL — ON AN
   * UNCONVERTED RESPONSE.** The row's amount describes one currency; a company
   * billed in two is on this list twice, once under each, and neither figure is
   * "what they pay us". A reader with no mark on the row has no way to know
   * which they are looking at. Empty for the ordinary company, which is most of
   * them.
   *
   * 🔴 **EMPTY ON EVERY CONVERTED RESPONSE, AND THAT IS THE FUNCTION WORKING
   * RATHER THAN THE MARK BEING IGNORED.** The sibling row it pointed at has been
   * summed into this one, so the figure is no longer partial and a badge calling
   * it partial would send a reader looking for a row that does not exist.
   * `currenciesUsedBy` genuinely finds one currency, because there genuinely is
   * one.
   *
   * ⛔ **DO NOT GATE IT ON A `converted` FLAG.** Following the DATA is what makes
   * it return of its own accord against a server that does not convert — which is
   * exactly the response that still needs it. A flag would take it away there
   * too.
   */
  alsoBilledIn: RevenueCurrency[];
  /** How many cycles in scope this company has revenue in. At least 1. */
  activeCycles: number;
  /**
   * This company's position in the REVENUE ranking of the population on
   * screen, 1 for the largest.
   *
   * 🔴 **CARRIED ON THE ROW RATHER THAN TAKEN FROM THE RENDER INDEX, because
   * the table can be ordered by something other than revenue.** Under
   * *smallest first* or *A to Z* a positional index would print a "rank" that
   * ranks nothing — a column of confident numbers describing the sort the
   * reader happens to have chosen. Read off the row it cannot say that: it is
   * the revenue rank under every ordering, which is also what makes the
   * scattered numbers under an alphabetical sort *tell* the reader that the
   * column is a revenue rank.
   *
   * ⚠️ **ASSIGNED AFTER EVERY FILTER, so it describes the population on
   * screen** — a floor that removes the bottom half must not leave the survivors
   * ranked 1, 4, 9 against companies nobody can see.
   */
  rank: number;
}

/**
 * The cycles a scope covers.
 *
 * `cycleKey` is {@link ALL} for every cycle, or one cycle's own name. An
 * unrecognised name — a stale saved link, a cycle an administrator retired —
 * falls back to every cycle rather than to none: an empty report reads as a
 * business that traded nothing, which is the opposite of what a broken filter
 * means.
 */
export function cyclesInScope(
  cycles: readonly RevenueCycle[],
  cycleKey: string,
): RevenueCycle[] {
  if (cycleKey === ALL) return [...cycles];
  const one = cycles.find((cycle) => cycle.name === cycleKey);
  return one === undefined ? [...cycles] : [one];
}

/**
 * Every company with revenue in this currency and this cycle scope, ranked
 * largest first.
 *
 * ⚠️ **THE RANK IS TAKEN ON A `Number`, AND THE FIGURE IS NOT.** Ordering needs
 * a comparison and nothing else; the string is what gets rendered. Sorting on
 * the string itself would order `"9.00"` above `"10000.00"` lexicographically,
 * which is a ranked list that is simply wrong with nothing on screen saying so.
 *
 * The tie-break is the company NAME rather than nothing, so two companies on an
 * identical figure do not swap places between renders — a ranked table whose
 * rows reorder under the reader is indistinguishable from data changing.
 */
export function scopeRows(
  rows: readonly RevenueTrackerRow[],
  scopeCycles: readonly RevenueCycle[],
  currency: RevenueCurrency,
): ScopedRow[] {
  const scoped: Omit<ScopedRow, 'rank'>[] = [];

  for (const row of rows) {
    const amount = revenueTotalFor(row, [...scopeCycles], currency);
    if (amount === null) continue;

    scoped.push({
      row,
      amount,
      alsoBilledIn: currenciesUsedBy(row).filter((code) => code !== currency),
      activeCycles: scopeCycles.filter(
        (cycle) => revenueFor(row, cycle.name, currency) !== null,
      ).length,
    });
  }

  scoped.sort((a, b) => {
    const delta = Number(b.amount) - Number(a.amount);
    if (delta !== 0) return delta;
    return a.row.company.name.localeCompare(b.row.company.name);
  });

  /*
   * ⚠️ **THE RANK IS STAMPED HERE AND NOWHERE ELSE**, on the one array that is
   * definitionally in revenue order. Everything downstream — the tiles, the
   * table, any re-ordering — reads it rather than recomputing it, so there is
   * one answer to "where does this company come" and no second copy to drift.
   */
  return scoped.map((entry, index) => ({ ...entry, rank: index + 1 }));
}

/** The exact sum of a scoped set. `null` when the set is empty. */
export function totalOf(scoped: readonly ScopedRow[]): string | null {
  return sumDecimalStrings(scoped.map((entry) => entry.amount));
}

/**
 * How many companies took NO revenue at all in the chosen unit.
 *
 * 🔴 **AN UNCONVERTED-ONLY MEASUREMENT SINCE 2026-09-18, AND ITS CALLER MUST
 * SHORT-CIRCUIT IT UNDER CONVERSION.** It exists because the currency control
 * used to HIDE COMPANIES and nothing else reported it — measured on production:
 * 652 USD invoices against 89 EUR and 56 GBP, so the GBP view legitimately
 * dropped most of the estate, and a shorter list is indistinguishable from a
 * quiet year. **Converted, the control hides nothing**: every company with any
 * paid revenue has revenue in the base.
 *
 * ⛔ **BUT IT WOULD NOT READ `0` FOR CERTAIN, WHICH IS WHY THE PANEL PASSES `0`
 * EXPLICITLY RATHER THAN TRUSTING IT TO.** A company whose every line carries an
 * UNSTATED unit (`d_invoices.currency` is nullable) is admitted by no gate in any
 * mode, so it would be counted here and reported as *"took no GBP and is not
 * listed"* — a sentence naming a currency filter that no longer exists, over a
 * company whose real problem is an invoice with no currency on it.
 *
 * ⚠️ **NOT DELETED, AND THE UNCONVERTED PATH IS THE WHOLE REASON.** A server
 * predating `baseCurrency` really does filter and really does need this count;
 * it is the one response on which nothing else on screen would say so.
 *
 * ⚠️ **TAKEN OVER EVERY CYCLE, NOT THE CYCLE SCOPE**, and deliberately: it
 * answers *"who is missing because of the CURRENCY"*. Narrowing it by cycle
 * would fold in companies absent for an ordinary reason — they did not trade
 * that year — and turn a fact about the control into a fact about the business.
 */
export function excludedCompanyCount(
  rows: readonly RevenueTrackerRow[],
  cycles: readonly RevenueCycle[],
  currency: RevenueCurrency,
): number {
  let excluded = 0;
  for (const row of rows) {
    if (revenueTotalFor(row, [...cycles], currency) === null) excluded += 1;
  }
  return excluded;
}

/* -------------------------------------------------------------------------
 * Narrowing the population — the floor, and "still trading"
 * --------------------------------------------------------------------- */

/**
 * A revenue floor as a number, or `null` for no floor.
 *
 * ⚠️ **AN UNPARSABLE OR NEGATIVE ENTRY IS *NO FLOOR*, NEVER A REFUSAL.** This
 * is a live-filtering box somebody types into a character at a time, and
 * refusing a half-typed value would empty the table under the cursor. It is
 * also why a box holding `abc` does not count towards the bar's active-filter
 * badge: nothing is being narrowed, so claiming a filter is in force would be
 * the badge lying in the direction that matters least but lying all the same.
 *
 * ⚠️ **`Number()` HERE PRODUCES A THRESHOLD, NEVER A FIGURE**, exactly as
 * {@link shareOf} produces a ratio. The amounts it is compared against stay the
 * exact decimal strings they arrived as.
 *
 * ⚠️ **DELIBERATELY NOT IMPORTED FROM `top-movers/top-movers`, which exports an
 * identical parser.** Same call `shareText` makes about `financial-shared`'s
 * `pctText`: five lines of parsing is a cheaper price than this tab taking a
 * dependency on another tab's module, and the two floors are not the same rule
 * anyway — that one tests either of two cycles, this one tests one figure. The
 * WORDING on screen is matched to it on purpose; the code is not shared.
 */
export function parseFloor(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * The companies whose revenue in this scope reaches the floor.
 *
 * 🔴 **MEASURED AGAINST THE FIGURE ON SCREEN, so the control means what it
 * says.** A floor tested against a company's lifetime total while the reader is
 * looking at one cycle would leave rows below the threshold visibly on screen,
 * which reads as a filter that does not work.
 *
 * ⚠️ **THE CONSEQUENCE, STATED: the cycle scope therefore decides which
 * companies pass.** Moving the cycle picker with a floor set changes the
 * population and so changes every bar in the cycle strip. That is coherent —
 * the reader asked for *accounts above X in this scope* — and it is why the
 * floor is applied to the company set BEFORE the strip is built rather than
 * only to the table: a strip covering companies the table excludes is two
 * panels describing two populations under one heading.
 *
 * ⚠️ **WITH NO FLOOR THE ROWS ARE RETURNED UNTOUCHED**, which is the ordinary
 * case and is bit-for-bit the behaviour that existed before this control did.
 */
export function floorRows(
  rows: readonly RevenueTrackerRow[],
  scopeCycles: readonly RevenueCycle[],
  currency: RevenueCurrency,
  floor: number | null,
): readonly RevenueTrackerRow[] {
  if (floor === null) return rows;

  return rows.filter((row) => {
    const amount = revenueTotalFor(row, [...scopeCycles], currency);
    if (amount === null) return false;
    const value = Number(amount);
    return Number.isFinite(value) && value >= floor;
  });
}

/**
 * The cycle the business is in today — the one containing today's date, or the
 * most recent one when today falls outside every window.
 *
 * ⚠️ **THE FALLBACK IS NOT COSMETIC.** Cycle windows are admin-maintained rows
 * and a gap between two of them is an ordinary state, not a fault; a control
 * that vanished on the days nobody had extended the calendar would read as a
 * broken page. The same fallback `comparisonCycles` already makes, for the same
 * reason.
 *
 * ⚠️ **NEVER `cycles[cycles.length - 1]` ALONE.** The list legitimately runs
 * ahead of today — a 2027-28 cycle can be catalogued long before it starts — so
 * the last row is *the furthest future*, and calling that "trading now" would
 * name a window in which nothing has happened yet.
 *
 * `null` only when there are no cycles at all, which is the state in which this
 * whole tab has nothing to show.
 */
export function currentCycleOf(cycles: readonly RevenueCycle[]): RevenueCycle | null {
  return cycleContaining([...cycles], todayIso()) ?? cycles[cycles.length - 1] ?? null;
}

/**
 * Was this company paid in this cycle, in the unit the figures are in?
 *
 * ⚠️ **`null` IS "NOTHING IN THIS UNIT" AND IS THEREFORE AN HONEST `false`.**
 *
 * 🔴 **THE ANSWER STOPPED DEPENDING ON THE PICKER ON 2026-09-18, AND THAT IS AN
 * IMPROVEMENT WORTH NAMING.** ⚠️ *SUPERSEDED, the consequence and not the rule:
 * "the *same* company can be 'still trading' under USD and not under GBP, and
 * both answers are right."* That was true while the currency filtered, and it
 * meant a control about the POPULATION silently answered differently depending on
 * a control about the UNIT. Converted, every currency is in the figures, so "is
 * this account still trading" has one answer whichever base the reader picks.
 *
 * ⚠️ **It reverts against a server that does not convert**, where both answers
 * are right again for the old reason.
 */
export function tradedIn(
  row: RevenueTrackerRow,
  cycle: RevenueCycle,
  currency: RevenueCurrency,
): boolean {
  return revenueFor(row, cycle.name, currency) !== null;
}

/* -------------------------------------------------------------------------
 * How the table is ordered
 * --------------------------------------------------------------------- */

/** The orderings the table offers. */
export type RevenueCompanySort = 'revenue-desc' | 'revenue-asc' | 'company';

/**
 * The default, and the one the RANK is taken in.
 *
 * ⚠️ Changing this does **not** change what `ScopedRow.rank` means — the rank
 * is the revenue rank whatever the reader is sorting by. See {@link ScopedRow}.
 */
export const DEFAULT_COMPANY_SORT: RevenueCompanySort = 'revenue-desc';

/**
 * The ordering options, written once so the control and the parser cannot
 * offer different sets.
 *
 * ⚠️ **THE LABELS NAME THE COLUMN AND THE DIRECTION**, because "Revenue" alone
 * on a table that is already revenue-ranked says nothing about what pressing it
 * would do. *Smallest first* is the one that earns the control: it is the only
 * way to see the accounts that barely trade, which is precisely the population
 * the floor exists to remove and somebody has to be able to look at.
 */
export const REVENUE_COMPANY_SORTS: readonly {
  value: RevenueCompanySort;
  label: string;
}[] = [
  { value: 'revenue-desc', label: 'Paid revenue — largest first' },
  { value: 'revenue-asc', label: 'Paid revenue — smallest first' },
  { value: 'company', label: 'Company name — A to Z' },
];

/** An unrecognised ordering — a stale link, a truncated URL — is the default. */
export function parseCompanySort(raw: string | null): RevenueCompanySort {
  return REVENUE_COMPANY_SORTS.some((option) => option.value === raw)
    ? (raw as RevenueCompanySort)
    : DEFAULT_COMPANY_SORT;
}

/**
 * A copy of the scoped rows in the reader's chosen order.
 *
 * 🔴 **A COPY, AND THE REVENUE-RANKED ARRAY IS NEVER RE-SORTED IN PLACE.** The
 * headline tiles read concentration off the ranked array — `topShareOf` takes
 * `slice(0, 10)` and means *the ten largest*. Sorted alphabetically in place,
 * that slice becomes *the first ten names in the alphabet* and the tile reports
 * a confident, wrong, entirely plausible percentage with nothing on screen to
 * contradict it. The ordering is a presentation choice belonging to the table
 * and it stops there.
 *
 * ⚠️ **EVERY ORDERING TIE-BREAKS ON THE COMPANY NAME**, so two companies on an
 * identical figure hold their places between renders — a ranked table whose
 * rows swap under the reader is indistinguishable from the data changing.
 */
export function orderRows(
  scoped: readonly ScopedRow[],
  sort: RevenueCompanySort,
): ScopedRow[] {
  const ordered = [...scoped];

  if (sort === 'company') {
    return ordered.sort((a, b) => a.row.company.name.localeCompare(b.row.company.name));
  }

  const direction = sort === 'revenue-asc' ? -1 : 1;
  return ordered.sort((a, b) => {
    /* ⚠️ `Number` for the comparison only — see the note on `scopeRows`. */
    const delta = (Number(b.amount) - Number(a.amount)) * direction;
    if (delta !== 0) return delta;
    return a.row.company.name.localeCompare(b.row.company.name);
  });
}

/* -------------------------------------------------------------------------
 * Shares, and where a percentage is a fiction
 * --------------------------------------------------------------------- */

/**
 * `part / whole` as a percentage, or `null` where no percentage exists.
 *
 * ⚠️ **`null` IN THREE REAL CASES, AND NONE OF THEM IS `0%`.** No total at all
 * (nothing in scope), a total that is exactly zero (a fully discounted or
 * refunded set — dividing by it is an infinity), and a malformed figure. Each
 * renders as an em dash. Printing `0%` for any of them states a share that was
 * never computed.
 *
 * ⚠️ **NOT CLAMPED.** A share cannot exceed 100 today, but clamping a figure
 * because it "should not" happen is how a real defect becomes invisible. Only
 * the BAR's geometry is clamped, in the component that draws it.
 *
 * ⚠️ **`Number()` HERE IS THE SANCTIONED EXCEPTION.** A ratio is not money and
 * is never rendered as an amount; the string it came from is what the reader
 * sees. Do not extend this to a figure.
 */
export function shareOf(part: string, whole: string | null): number | null {
  if (whole === null) return null;
  const partNum = Number(part);
  const wholeNum = Number(whole);
  if (!Number.isFinite(partNum) || !Number.isFinite(wholeNum) || wholeNum === 0) return null;
  return (partNum / wholeNum) * 100;
}

/**
 * A share as text, to one decimal place.
 *
 * ⚠️ **AN EM DASH FOR `null`, WHICH IS "NO FIGURE" AND NEVER "ZERO".**
 *
 * ⚠️ **NOT `pctText` FROM `financial/financial-shared`, deliberately.** That
 * module is the Financial Analytics tab's and pulls `@/lib/targets`,
 * `team-contribution` and `use-team-performance` in behind it — a whole tree
 * this tab has no business depending on, in a folder another view owns. Four
 * lines of formatter is a cheaper price than a coupling between two tabs that
 * answer different questions from different endpoints.
 */
export function shareText(share: number | null): string {
  if (share === null || !Number.isFinite(share)) return '—';
  return `${Math.round(share * 10) / 10}%`;
}

/**
 * The combined share of the N largest accounts, as a percentage.
 *
 * ⚠️ **`null` WHEN THE SET IS NO LARGER THAN N**, because the answer would then
 * be a tautological 100% — "the top ten account for all of it" is true of any
 * nine companies and says nothing about concentration. A dash and a stated
 * reason is the honest render; a confident 100% is not.
 */
export function topShareOf(
  scoped: readonly ScopedRow[],
  n: number,
  total: string | null,
): number | null {
  if (scoped.length <= n) return null;
  const head = sumDecimalStrings(scoped.slice(0, n).map((entry) => entry.amount));
  return head === null ? null : shareOf(head, total);
}

/**
 * The middle account's figure — the typical size of an account on screen.
 *
 * 🔴 **THE LOWER MEDIAN, AND NEVER A MEAN OR AN AVERAGE OF TWO MIDDLES.** With
 * an even number of companies the two middle figures are averaged by the
 * textbook definition, and that would print a figure **no account was ever
 * paid** — an invented amount, to the cent, sitting in a tile beside three real
 * ones. Taking the lower of the two is exact, is a real company's real figure,
 * and errs towards the smaller account, which is the safe direction for a tile
 * whose job is to show how small the typical account is next to the total.
 *
 * ⚠️ **AN AVERAGE WOULD ALSO HAVE BEEN THE WRONG STATISTIC.** Total ÷ companies
 * is derivable from the two tiles beside it, so it would be a restatement; and
 * on a set this skewed — one account at 12% of everything — the mean describes
 * nobody. The median is the only one of the three that is new information.
 *
 * ⚠️ **SORTS ITS OWN COPY rather than trusting the caller's order.** The array
 * it is handed is revenue-ranked today and this function would silently start
 * answering something else the day it is handed an alphabetical one.
 *
 * `null` for an empty set, which is the ordinary answer for a currency nobody
 * was billed in — never `"0.00"`.
 */
export function medianAmountOf(scoped: readonly ScopedRow[]): string | null {
  if (scoped.length === 0) return null;

  const ranked = [...scoped].sort((a, b) => Number(b.amount) - Number(a.amount));
  /*
   * Descending, so `floor(n / 2)` is the true middle for an odd count and the
   * LOWER of the two middles for an even one. Worked: [100, 80, 60, 40] → index
   * 2 → 60; [100, 80, 60, 40, 20] → index 2 → 60.
   */
  return ranked[Math.floor(ranked.length / 2)]?.amount ?? null;
}

/* -------------------------------------------------------------------------
 * The cycle profile
 * --------------------------------------------------------------------- */

/**
 * One cycle's figure for the rows currently on screen.
 *
 * 🔴 **NOTHING IN THIS FOLDER PRODUCES ONE ANY MORE, AND THIS TYPE IS STILL
 * LOAD-BEARING.** *SUPERSEDED 2026-09-13: `cycleTotalsOf(rows, cycles,
 * currency)`, which summed `revenueFor` over every cycle.* The owner removed the
 * "Revenue by cycle" card from the Revenue by Company tab, and that memo was its
 * only caller, so the producer went with it.
 *
 * ⛔ **DO NOT DELETE THIS TYPE OR {@link peakOf} AS DEAD CODE.**
 * `RevenueCycleStrip` — which lives beside this file and is **still mounted, by
 * the Revenue by PRODUCT tab** — types its `totals` prop on this interface and
 * scales its bars with `peakOf`. The product tab supplies the array from its
 * OWN `cycleTotalsOf` in `revenue-product.ts`, so the shape is the contract
 * between two folders and only the producer was local.
 *
 * ⚠️ **So "no producer here" is exactly what this is supposed to look like**, and
 * a grep that finds the type constructed nowhere in this folder has found the
 * design rather than a leftover.
 */
export interface CycleTotal {
  cycle: RevenueCycle;
  /** An exact decimal string, or `null` for "nothing in this currency". */
  amount: string | null;
}

/**
 * The largest figure in a set of cycle totals, for scaling a bar.
 *
 * `0` when there is nothing to scale against, which every caller must treat as
 * "draw no bar" rather than dividing by it.
 *
 * ⚠️ **Its one caller is `RevenueCycleStrip`, which this tab no longer mounts** —
 * see {@link CycleTotal}. It is reached from the Revenue by Product tab.
 */
export function peakOf(totals: readonly CycleTotal[]): number {
  let peak = 0;
  for (const entry of totals) {
    if (entry.amount === null) continue;
    const value = Number(entry.amount);
    if (Number.isFinite(value) && value > peak) peak = value;
  }
  return peak;
}

/* -------------------------------------------------------------------------
 * What a company bought
 * --------------------------------------------------------------------- */

/** One cycle of a company's detail, already filtered to the chosen currency. */
export interface CycleDetail {
  cycle: RevenueCycle;
  amount: string;
  services: RevenueTrackerService[];
}

/**
 * A company's cycle-by-cycle detail, in date order, in one currency.
 *
 * 🔴 **`serviceName` IS A COMPOSED LABEL — the PROJECT's name where the line
 * names a project, otherwise the PRODUCT's — AND IT IS THE ONLY PRODUCT OR
 * PROJECT INFORMATION THIS ENDPOINT CARRIES.** So it answers *"which
 * products/projects is this company in"* and it cannot answer anything else:
 *
 * ⛔ **NEVER GROUP OR SUM BY IT.** The same product sold bare and sold through a
 * project produces two different strings, so a "revenue by product" figure
 * built from it splits one product across several rows and is wrong with
 * nothing on screen to show it. Revenue by product is its own tab with its own
 * endpoint.
 *
 * ⛔ **NEVER DE-DUPLICATE ON IT, AND NEVER KEY A ROW ON IT.** Two lines under
 * one project read IDENTICALLY and are still two sales with their own amounts
 * and their own invoice numbers; collapsing them loses money off the page.
 *
 * Cycles with nothing in this currency are dropped entirely rather than carried
 * as empty — an expanded company shows the years it traded, and a run of em
 * dashes for the years it did not is noise, not information.
 */
export function cycleDetailsOf(
  row: RevenueTrackerRow,
  scopeCycles: readonly RevenueCycle[],
  currency: RevenueCurrency,
): CycleDetail[] {
  const details: CycleDetail[] = [];

  for (const cycle of scopeCycles) {
    const amount = revenueFor(row, cycle.name, currency);
    if (amount === null) continue;
    details.push({
      cycle,
      amount,
      /*
       * ⚠️ Filtered to the chosen currency, exactly as the figure above it was.
       * Listing every line while the total counts only some would put a set of
       * services on screen that visibly do not add up to the number beside
       * them, and the reader would be right to distrust the number.
       */
      services: (row.cycles[cycle.name]?.services ?? []).filter(
        (service) => service.currency === currency,
      ),
    });
  }

  return details;
}

/* -------------------------------------------------------------------------
 * The products × cycles matrix inside an expanded company
 * --------------------------------------------------------------------- */

/**
 * How an expanded company is shown: the products × cycles matrix, or the
 * per-cycle list of lines that {@link cycleDetailsOf} feeds.
 */
export type CompanyExpandView = 'table' | 'lines';

/**
 * Where the reader's choice is remembered, per browser.
 *
 * ⚠️ **THE STRING IS THE STORAGE KEY**; renaming it drops every saved choice.
 */
export const COMPANY_EXPAND_VIEW_KEY = 'emerge.revenueCompany.expandView';

/** The stored choice, or the default (`table`). Storage can throw; it is a convenience only. */
export function readCompanyExpandView(): CompanyExpandView {
  try {
    return window.localStorage.getItem(COMPANY_EXPAND_VIEW_KEY) === 'lines' ? 'lines' : 'table';
  } catch {
    return 'table';
  }
}

export function writeCompanyExpandView(view: CompanyExpandView): void {
  try {
    window.localStorage.setItem(COMPANY_EXPAND_VIEW_KEY, view);
  } catch {
    /* Private window or blocked storage: the choice lasts this page only. */
  }
}

/**
 * The cycle containing today, strictly — `null` when today falls in no window.
 *
 * ⚠️ **NOT {@link currentCycleOf}**, which falls back to the latest cycle for the
 * "still trading" control. The matrix marks the column a reader is living in,
 * and marking a cycle that does not contain today would be a claim, not a
 * fallback.
 */
export function todaysCycleOf(cycles: readonly RevenueCycle[]): RevenueCycle | null {
  return cycleContaining([...cycles], todayIso());
}

/** The matrix's columns: the cycles in scope, already in date order. */
export function matrixCyclesOf(scopeCycles: readonly RevenueCycle[]): MatrixCycle[] {
  return scopeCycles.map((cycle) => ({
    id: String(cycle.id),
    name: cycle.name,
    start: cycle.startDate,
  }));
}

/**
 * Whether this server reports a product on every line of this company.
 *
 * 🔴 **`product: undefined` IS AN OLDER API, AND THE MATRIX IS WITHHELD ON IT.**
 * The matrix rows ARE products; built from lines that do not say which product
 * they sold, every line would land in the no-product row — a confident wrong
 * answer. The Lines view is then the only view, with no toggle.
 */
export function reportsProducts(row: RevenueTrackerRow): boolean {
  for (const cell of Object.values(row.cycles)) {
    for (const service of cell?.services ?? []) {
      if (service.product === undefined) return false;
    }
  }
  return true;
}

/**
 * A company's lines in the shape `buildRevenueMatrix` takes.
 *
 * 🔴 **EXACTLY THE LINES THE LINES VIEW LISTS, AT EXACTLY ITS AMOUNTS** — the
 * same {@link cycleDetailsOf} walk: every cycle in scope, every service in the
 * unit the figures are in, `paidFigureOf(service)` (paid, incl. tax — owner,
 * 2026-10-16). So the matrix's grand total is the company row's total by
 * construction (`revenueTotalFor` sums the same lines through the same
 * `paidFigureOf`), and the two views cannot describe different money.
 *
 * ⚠️ `isUncatalogued` / `lineDescription` are optional on the wire:
 * `undefined` is an older API and reads as `false` / `null`.
 */
export function matrixLinesOf(
  row: RevenueTrackerRow,
  scopeCycles: readonly RevenueCycle[],
  currency: RevenueCurrency,
): MatrixLine[] {
  const lines: MatrixLine[] = [];

  for (const detail of cycleDetailsOf(row, scopeCycles, currency)) {
    const cycleId = String(detail.cycle.id);
    for (const service of detail.services) {
      /* `null` is a line with no incl-tax figure: `revenueFor` skips it too. */
      const figure = paidFigureOf(service);
      if (figure === null) continue;
      const product = service.product ?? null;
      lines.push({
        cycleId,
        productId: product === null ? null : String(product.id),
        productName: product === null ? null : product.name,
        isUncatalogued: service.isUncatalogued ?? false,
        lineDescription: service.lineDescription ?? null,
        amount: figure,
        invoiceNumber: service.invoiceNumber,
        invoiceId: service.invoiceId === null ? null : String(service.invoiceId),
      });
    }
  }

  return lines;
}

/**
 * Does this company match what somebody typed in the table's search box?
 *
 * ⚠️ **MATCHES ANYTHING ON THE ROW** (owner 2026-10-27, superseding the
 * company-name-only rule): the company name, the billing region, and every
 * line's service, product, description and invoice number in any cycle. The
 * box is labelled "Search", not "Search companies", so the promise matches.
 *
 * Case-insensitive substring. No wildcard handling: this runs in the browser
 * over rows already in hand, so there is no `LIKE` for a `%` to escape.
 */
export function matchesSearch(row: RevenueTrackerRow, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') return true;
  const has = (text: string | null | undefined) => text != null && text.toLowerCase().includes(needle);
  if (has(row.company.name) || has(regionLabelOf(row))) return true;
  return Object.values(row.cycles).some((cell) =>
    (cell?.services ?? []).some(
      (service) =>
        has(service.serviceName) ||
        has(service.product?.name) ||
        has(service.lineDescription) ||
        has(service.invoiceNumber),
    ),
  );
}
